import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { logUserLogin } from "@/utils/analyticsUtils";
import { getStoredUtmParams, clearUtmParams, getUtmParamsFromCookies } from "@/utils/utmUtils";
import { redirectAfterAuth } from "@/lib/authRedirect";
import { useTranslation } from "react-i18next";

export const AuthCallback = () => {
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processingComplete, setProcessingComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingStart, setProcessingStart] = useState<number | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    let isMounted = true;
    
    const handleOAuthCallback = async () => {
      if (processingComplete) return;
      
      try {
        // Log only that we're processing a callback, not the params themselves
        console.log("AuthCallback: Processing authentication callback");
        
        // Get stored UTM parameters (cookies first, localStorage fallback)
        const storedUtmParams = getStoredUtmParams();
        console.log("Retrieved UTM parameters for auth callback:", storedUtmParams);
        
        // Check for error in the URL (might be present if auth failed)
        const errorParam = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");
        
        if (errorParam) {
          console.error("Authentication error:", errorDescription);

          if(errorDescription.includes("Database error saving new user")) {
            localStorage.setItem("oauthError", "This account does not exist. Make sure you have signed up before logging in.");
          }
          else {
            localStorage.setItem("oauthError", "Could not sign in with this account. Make sure you have signed up before logging in.");
          }
          toast({
            variant: "destructive",
            title: t('auth.authError'),
            description: errorDescription || t('auth.failedToAuthenticate'),
          });
          navigate("/auth", { replace: true });
          return;
        }

        // Check if this is an email verification callback (type=signup or type=recovery)
        const callbackType = searchParams.get("type");
        
        if (callbackType === "signup" || callbackType === "recovery") {
          console.log(`Processing ${callbackType} verification`);
          
          // Exchange code for session if present
          if (searchParams.has("code")) {
            console.log("Exchanging verification code for session");
            
            try {
              const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(
                searchParams.get("code") || ""
              );
              
              if (exchangeError) {
                console.error("Verification code exchange error:", exchangeError.message);
                setError("Failed to verify email");
                toast({
                  variant: "destructive",
                  title: "Verification Error",
                  description: "There was a problem with your email verification. Please try again.",
                });
                navigate("/auth", { replace: true });
                return;
              }
              
              // Success - email verified
              toast({
                title: callbackType === "signup" ? "Email Verified!" : "Recovery Complete",
                description: callbackType === "signup" 
                  ? "Your email has been verified. You can now sign in." 
                  : "Your password has been reset. You can now sign in with your new password.",
              });
              
              // Get user from session
              const { data: { session } } = await supabase.auth.getSession();
              
              if (session?.user) {
                // Log the successful verification
                await logUserLogin(session.user.id);
                
                if (callbackType === "signup") {
                  // For new users, redirect to simple-onboarding
                  navigate("/simple-onboarding", { replace: true });
                } else {
                  // Use redirectAfterAuth for password reset
                  redirectAfterAuth(navigate);
                }
              } else {
                // No session yet (rare case)
                navigate("/auth", { replace: true });
              }
            } catch (error) {
              console.error("Error processing verification:", error);
              navigate("/auth", { replace: true });
            }
            return;
          }
        }

        // If no code is present, fall back to session check
        if (searchParams.has("code")) {
          console.log("Exchanging authorization code for session");
          
          try {
            // Exchange the authorization code for a session without logging the code
            const { data: authData, error: exchangeError } = await supabase.auth.exchangeCodeForSession(
              searchParams.get("code") || ""
            );
            
            if (exchangeError) {
              console.error("Session exchange error:", exchangeError.message);
              setError("Failed to complete authentication");
              toast({
                variant: "destructive",
                title: "Authentication Error",
                description: "Failed to complete authentication. Please try again.",
              });
              navigate("/auth", { replace: true });
              return;
            }
            
            if (!authData.session) {
              console.error("No session established after code exchange");
              setError("Authentication process incomplete");
              toast({
                variant: "destructive",
                title: "Authentication Error",
                description: "Failed to establish a session. Please try again.",
              });
              navigate("/auth", { replace: true });
              return;
            }

            console.log("Authentication successful, retrieving user details");
            
            // Log the successful login without exposing sensitive data
            if (authData.session.user) {
              await logUserLogin(authData.session.user.id);
              
              // Extract user information safely
              const user = authData.session.user;
              
              // Save name information from OAuth if available
              let firstName = '';
              let lastName = '';
              
              if (user.app_metadata?.provider === 'google') {
                // Get name data from metadata
                firstName = user.user_metadata?.full_name?.split(' ')[0] || 
                           user.user_metadata?.name?.split(' ')[0] || 
                           user.user_metadata?.given_name || '';
                           
                lastName = user.user_metadata?.full_name?.split(' ').slice(1).join(' ') || 
                          user.user_metadata?.name?.split(' ').slice(1).join(' ') || 
                          user.user_metadata?.family_name || '';
              }
              
              // Get UTM parameters with fallbacks:
              // 1. From cookies (primary method via storedUtmParams)
              // 2. From user metadata (if passed by Google/phone signup)
              // 3. From URL parameters (if present in the callback URL)
              const utm_source = storedUtmParams.utm_source || 
                                user.user_metadata?.utm_source || 
                                searchParams.get("utm_source") || 
                                null;
                                
              const utm_medium = storedUtmParams.utm_medium || 
                               user.user_metadata?.utm_medium || 
                               searchParams.get("utm_medium") || 
                               null;
                               
              const utm_campaign = storedUtmParams.utm_campaign || 
                                 user.user_metadata?.utm_campaign || 
                                 searchParams.get("utm_campaign") || 
                                 null;
                                 
              const utm_content = storedUtmParams.utm_content || 
                                user.user_metadata?.utm_content || 
                                searchParams.get("utm_content") || 
                                null;
                                
              const utm_term = storedUtmParams.utm_term || 
                             user.user_metadata?.utm_term || 
                             searchParams.get("utm_term") || 
                             null;
                             
              const referrerValue = storedUtmParams.referrer || 
                                  user.user_metadata?.referrer || 
                                  searchParams.get("referrer") || 
                                  null;
              
              console.log("Saving UTM parameters to profiles table:", {
                utm_source,
                utm_medium,
                utm_campaign,
                utm_content,
                utm_term,
                referrer: referrerValue
              });
              
              // Update the profile with the user's information (no logging of personal data)
              const { error: profileUpdateError } = await supabase
                .from('profiles')
                .update({ 
                  email: user.email,
                  first_name: firstName || null,
                  last_name: lastName || null,
                  // Include UTM parameters
                  utm_source,
                  utm_medium,
                  utm_campaign,
                  utm_content,
                  utm_term,
                  referrer: referrerValue
                })
                .eq('id', user.id)
                .select();
                
              if (profileUpdateError) {
                console.error("Error updating profile:", profileUpdateError.message);
                // Try to create the profile if update fails
                console.log("Attempting to create profile since update failed");
                const { error: insertError } = await supabase
                  .from('profiles')
                  .insert([{ 
                    id: user.id, 
                    email: user.email,
                    first_name: firstName || null,
                    last_name: lastName || null,
                    // Include UTM parameters in insert as well
                    utm_source,
                    utm_medium,
                    utm_campaign,
                    utm_content,
                    utm_term,
                    referrer: referrerValue
                  }]);
                  
                if (insertError) {
                  console.error("Error creating profile:", insertError);
                } else {
                  console.log("Profile created successfully with email and UTM data");
                  // Clear stored UTM parameters after successful profile creation
                  clearUtmParams();
                }
              } else {
                console.log("Profile updated successfully with OAuth data and UTM parameters");
                // Clear stored UTM parameters after successful profile update
                clearUtmParams();
              }
              
              // Verify profile has email stored
              const { data: verifyProfile, error: verifyError } = await supabase
                .from('profiles')
                .select('email')
                .eq('id', user.id)
                .single();
                
              if (verifyError || !verifyProfile.email) {
                console.warn("Email verification failed or email missing, forcing update");
                // Force update the email field to ensure it's set
                const { error: emailUpdateError } = await supabase
                  .from('profiles')
                  .update({ email: user.email })
                  .eq('id', user.id);
                  
                if (emailUpdateError) {
                  console.error("Error in final email update:", emailUpdateError.message);
                } else {
                  console.log("Final email update succeeded");
                }
              } else {
                console.log("Email verification successful");
              }
            }
            
            // Notify n8n about successful authentication
            try {
              const userId = authData.session.user.id;
              await supabase.functions.invoke('n8n-webhook', {
                body: { uuid: userId, event: 'user_authenticated' }
              });
            } catch (n8nError) {
              console.error("Error notifying webhook:", n8nError instanceof Error ? n8nError.message : String(n8nError));
              // Continue with the auth flow even if notification fails
            }
            
            toast({
              title: "Welcome!",
              description: "You're now signed in.",
            });
            
            // Try to get profile to determine redirect
            try {
              const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('profession')
                .eq('id', authData.session.user.id)
                .maybeSingle();

              if (isMounted) {
                setProcessingComplete(true);
                
                // Navigate based on whether the user has completed onboarding
                if (!profile || profileError || !profile.profession) {
                  navigate("/simple-onboarding", { replace: true });
                } else {
                  // Use redirectAfterAuth to handle redirection
                  redirectAfterAuth(navigate);
                }
              }
            } catch (profileError) {
              // If there's an error getting the profile, default to onboarding
              console.error("Error retrieving profile data");
              if (isMounted) {
                setProcessingComplete(true);
                navigate("/simple-onboarding", { replace: true });
              }
            }
          } catch (exError) {
            console.error("Error processing authentication");
            setError("Authentication process failed");
            toast({
              variant: "destructive",
              title: "Error",
              description: "Something went wrong. Please try again.",
            });
            if (isMounted) {
              navigate("/auth", { replace: true });
            }
          }
        } else {
          // If no code is present, fall back to session check
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log("Found existing session, checking user profile");
            try {
              const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('profession')
                .eq('id', session.user.id)
                .maybeSingle();

              if (isMounted) {
                setProcessingComplete(true);
                
                if (!profile || profileError || !profile.profession) {
                  navigate("/simple-onboarding", { replace: true });
                } else {
                  // Use redirectAfterAuth to handle redirection
                  redirectAfterAuth(navigate);
                }
              }
            } catch (profileError) {
              if (isMounted) {
                setProcessingComplete(true);
                navigate("/simple-onboarding", { replace: true });
              }
            }
          } else {
            console.error("No authentication code or session found");
            setError("Missing authentication data");
            toast({
              variant: "destructive",
              title: "Authentication Error",
              description: "Could not complete the authentication process.",
            });
            if (isMounted) {
              navigate("/auth", { replace: true });
            }
          }
        }
      } catch (error) {
        console.error("Error in authentication callback");
        setError("Authentication error occurred");
        toast({
          variant: "destructive",
          title: "Error",
          description: "Something went wrong. Please try again.",
        });
        if (isMounted) {
          navigate("/auth", { replace: true });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    handleOAuthCallback();

    return () => {
      isMounted = false;
    };
  }, [navigate, toast, searchParams, processingComplete]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-pink-50 to-white p-4">
      <img 
        src="/lovable-uploads/bc4b57d4-e29b-4e44-8e1c-82ec09ca6fd6.png" 
        alt="Gebeya" 
        className="h-12 mb-8 animate-fade-up" 
      />
      <div className="w-full max-w-md text-center space-y-4">
        <h2 className="text-2xl font-bold">{t('auth.authenticating')}</h2>
        <p className="text-muted-foreground">{t('auth.pleaseWaitAuth')}</p>
        
        {error && (
          <div className="p-4 border border-red-200 bg-red-50 rounded-md text-left">
            <p className="text-red-700 font-medium">{t('auth.authError')}</p>
            <p className="text-red-600 text-sm">{error}</p>
            <p className="text-sm mt-2">{t('auth.authErrorPersists')}</p>
          </div>
        )}
        
        {/* Loading indicator */}
        <div className="flex justify-center mt-6">
          <div className="w-10 h-10 border-4 border-gebeya-pink border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    </div>
  );
};
